// Port of the app's `ReviseChecks` and `ReviseSwitches` (Apps/Shared/Rooms/LibraryModel+Revise.swift): the checks Revise ▸
// Checks offers, pass by pass, as the column lists them (`design-docs/Revise passes — design.md`), and which are switched
// on. A check is named by its id: a `CleanupCheck`, `BuiltInStyleCheck` or `VariantKind`, `quotes` (quotation marks never
// closed or opened), `spelling`, or `rule:<id>` (a habit).
import { BUILT_IN_STYLE_CHECKS, styleCheckName, styleCheckPass, styleCheckSummary, type BuiltInStyleCheck, type StyleSettings } from './style/linter.ts';
import { CLEANUP_CHECKS, cleanupCheckName, cleanupCheckPass, cleanupCheckSummary, type CleanupCheck, type RevisePass } from './textCleanup.ts';
import { VARIANT_KINDS, variantKindName, variantKindSummary, type VariantKind } from './variants.ts';

export const QUOTES_CHECK = 'quotes';
export const SPELLING_CHECK = 'spelling';

export const MISTAKE_CHECKS: readonly string[] = [SPELLING_CHECK, 'doubled-words', 'dialogue-punctuation', 'punctuation-spacing', QUOTES_CHECK, 'brackets', 'capitals',
  'verse-spaces', 'tense'];
export const TYPOGRAPHY_CHECKS: readonly string[] = [
  'curly-quotes', 'spaces', 'empty-lines', 'dashes', 'en-dash-ranges', 'ellipses', 'primes', 'multiplication', 'symbols', 'dialogue-quotes',
  'small-caps-times', 'underlining',
];
export const CONSISTENCY_CHECKS: readonly string[] = VARIANT_KINDS;
export const STYLE_CHECKS: readonly string[] = BUILT_IN_STYLE_CHECKS.filter((c) => styleCheckPass(c) === 'style');

/** The passes the column shows, in the order to work through them. */
export const COLUMN_PASSES: readonly RevisePass[] = ['mistakes', 'typography', 'consistency', 'style'];

export function checksIn(pass: RevisePass): readonly string[] {
  return { mistakes: MISTAKE_CHECKS, typography: TYPOGRAPHY_CHECKS, consistency: CONSISTENCY_CHECKS, style: STYLE_CHECKS }[pass];
}

const isCleanup = (c: string): c is CleanupCheck => (CLEANUP_CHECKS as readonly string[]).includes(c);
const isBuiltIn = (c: string): c is BuiltInStyleCheck => (BUILT_IN_STYLE_CHECKS as readonly string[]).includes(c);
const isVariant = (c: string): c is VariantKind => (VARIANT_KINDS as readonly string[]).includes(c);
export const isBuiltInStyleCheck = isBuiltIn;

/** `ReviseChecks.pass(of:)`. */
export function checkPass(check: string): RevisePass {
  if (check === QUOTES_CHECK || check === SPELLING_CHECK) return 'mistakes';
  if (isCleanup(check)) return cleanupCheckPass(check);
  if (isBuiltIn(check)) return styleCheckPass(check);
  if (isVariant(check)) return 'consistency';
  return 'style';
}

/** `ReviseChecks.name(of:)`. */
export function checkName(check: string): string {
  if (check === QUOTES_CHECK) return 'Quotes never closed';
  if (check === SPELLING_CHECK) return 'Spelling';
  if (isCleanup(check)) return cleanupCheckName(check);
  if (isBuiltIn(check)) return styleCheckName(check);
  if (isVariant(check)) return variantKindName(check);
  return check;
}

/** `ReviseChecks.shortName(of:)`: the name the column gives a check, short enough for its width. */
export function checkShortName(check: string): string {
  const short: Record<string, string> = {
    [QUOTES_CHECK]: 'Unclosed quotes', brackets: 'Unclosed brackets', 'punctuation-spacing': 'Punctuation spacing', capitals: 'Small letters',
    'curly-quotes': 'Curly quotes', spaces: 'Extra spaces', 'empty-lines': 'Empty lines', dashes: 'Dashes', ellipses: 'Ellipses', primes: 'Primes',
    symbols: '™ ® ©', 'dialogue-quotes': 'Dialogue marks', 'small-caps-times': 'a.m. and p.m.', 'spelling-variants': 'Spellings',
    'family-words': 'Mum and mum',
  };
  return short[check] ?? checkName(check);
}

/** `ReviseChecks.summary(of:)`. */
export function checkSummary(check: string): string {
  if (check === SPELLING_CHECK) {
    return "Words the spelling check doesn't know, in the project's language. Names, words in capitals, and words you use three times or more are left alone; the glossary, the cast and your dictionary are known.";
  }
  if (check === QUOTES_CHECK) return 'A quotation mark opened and never closed, or closed and never opened. A speech running over paragraphs is fine.';
  if (isCleanup(check)) return cleanupCheckSummary(check);
  if (isBuiltIn(check)) return styleCheckSummary(check);
  if (isVariant(check)) return variantKindSummary(check);
  return '';
}

/** Checks that are off until the writer turns them on (underlining may be meant). */
export const OFF_BY_DEFAULT: ReadonlySet<string> = new Set(['underlining']);

/** `ReviseChecks.fixesAllAtOnce`: whether a check's fixes are certain enough to make all at once (typography's, and
 *  numbers spelt out; the other consistency sets are made whole by choosing a form). */
export const fixesAllAtOnce = (check: string) => checkPass(check) === 'typography' || check === 'numbers';

/** `ReviseSwitches`: which checks are on, on this device. Built-in style checks and habits keep theirs in the project; the
 *  others are on unless switched off (`hidden`), or, for those off by default, off unless switched on (`shown`). */
export interface ReviseSwitches { hidden: ReadonlySet<string>; shown: ReadonlySet<string> }

export function isCheckOn(check: string, switches: ReviseSwitches, settings: StyleSettings): boolean {
  if (isBuiltIn(check)) return settings.builtIns.has(check);
  if (check.startsWith('rule:')) return settings.rules.some((r) => 'rule:' + r.id === check && r.enabled);
  return OFF_BY_DEFAULT.has(check) ? switches.shown.has(check) : !switches.hidden.has(check);
}
